import moment from "moment";

/**
 * Função genérica para gerar itens de chips a partir de filtros aplicados
 * @param filtros - Objeto contendo os filtros aplicados
 * @param filtersChipLabels - Mapeamento das chaves dos filtros para seus rótulos de exibição
 * @param omitFilters - Array de chaves de filtros que devem ser omitidos dos chips
 * @returns Array de strings formatadas para exibir nos chips
 */
export function getChipFilterItems<T extends Record<string, any>>(
    filtros: T,
    filtersChipLabels: Partial<Record<keyof T, string>>,
    omitFilters: (keyof T)[] = []
): string[] {
    const items: string[] = [];

    Object.entries(filtros).forEach(([key, value]) => {
        const filterKey = key as keyof T;

        if (value === undefined || value === null || value === '') {
            return;
        }

        if (omitFilters.includes(filterKey)) {
            return;
        }

        const label = filtersChipLabels[filterKey] || String(key);
        let displayValue: string;

        if (isNeverDateKey(key)) {
            displayValue = String(value);
        }

        else if (Array.isArray(value)) {
            if (isObject(value[0])) {
                displayValue = value.map(item => findObjectLabel(item)).join(', ');
            } else {
                displayValue = value.join(', ');
            }
        }

        else if (isDateKey(key) && isDateValue(value)) {
            displayValue = moment(value).format("DD/MM/YYYY");
        }

        else if (typeof value === 'boolean') {
            displayValue = value ? 'Sim' : 'Não';
        }

        else if (isObject(value)) {
            displayValue = findObjectLabel(value);
        }

        else {
            displayValue = String(value);
        }

        items.push(`${label}: ${displayValue}`);
    });

    return items;
}

function isObject(value: any): value is Record<string, any> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const DATE_KEYS = [
    'data',
    'data_inicio',
    'data_fim',
    'created_at',
    'updated_at',
    'data_cadastro',
    'data_nascimento',
];

function isDateKey(key: string): boolean {
    return DATE_KEYS.includes(key);
}

function isNeverDateKey(key: string): boolean {
    return [
        'documento_pessoa_pesquisar',
        'cpf',
        'cnpj',
        'documento',
        'codigo',
        'codigo_publico',
    ].includes(key);
}

function isDateValue(value: any): boolean {
    return moment.isMoment(value) || value instanceof Date;
}

function findObjectLabel(obj: Record<string, any>): string {
    const labelKeys = [
        'nome',
        'nome_pessoa',
        'name',
        'title',
        'titulo',
        'label',
        'description',
        'descricao',
        'texto',
        'valor',
        'value'
    ];

    for (const key of labelKeys) {
        if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
            return String(obj[key]);
        }
    }

    for (const value of Object.values(obj)) {
        if (typeof value === 'string' && value.trim() !== '') {
            return value;
        }
        if (typeof value === 'number') {
            return String(value);
        }
    }

    return JSON.stringify(obj);
}
